/**
 * İndirim Calculator Component
 * Handles discount calculation (original price + discount percentage)
 */

class IndirımCalculator {
  constructor() {
    this.form = document.getElementById('indirim-calculator-form');
    this.inputs = {
      originalPrice: document.getElementById('original-price'),
      customDiscount: document.getElementById('custom-discount')
    };
    
    // Discount percentage buttons
    this.discountButtons = document.querySelectorAll('.discount-btn');
    this.customDiscountContainer = document.getElementById('custom-discount-container');
    
    // Action buttons
    this.calculateBtn = document.getElementById('calculate-indirim');
    this.resetBtn = document.getElementById('reset-form');
    
    // Result elements
    this.resultSection = document.getElementById('indirim-result');
    this.resultElements = {
      originalPrice: document.getElementById('original-price-result'),
      discountPercentage: document.getElementById('discount-percentage-result'),
      discountAmount: document.getElementById('discount-amount-result'),
      finalPrice: document.getElementById('final-price-result')
    };

    // Current state
    this.currentDiscount = 10; // Default 10%
    this.isCustomDiscount = false;

    this.init();
  }

  init() {
    if (!this.form) return;

    this.bindEvents();
    this.setupRealTimeCalculation();
    this.initializeDefaultState();
  }

  initializeDefaultState() {
    // Set default discount button
    const defaultDiscountBtn = document.querySelector('.discount-btn[data-discount="10"]');
    if (defaultDiscountBtn) {
      defaultDiscountBtn.classList.add('active');
    }
  }

  bindEvents() {
    // Calculate button
    this.calculateBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.calculateDiscount();
    });

    // Reset button
    this.resetBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.resetForm();
    });


    // Discount percentage buttons
    this.discountButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleDiscountSelection(btn);
      });
    });

    // Form validation on input
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.addEventListener('input', () => {
          this.validateInput(input);
        });

        input.addEventListener('blur', () => {
          this.validateInput(input);
        });
      }
    });

    // Enter key submission
    this.form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.calculateDiscount();
      }
    });
  }

  setupRealTimeCalculation() {
    // Calculate as user types (with debouncing)
    let timeout;
    const triggerCalculation = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (this.isFormValid(false)) {
          this.calculateDiscount(true); // Silent calculation
        }
      }, 500);
    };

    // Original price input
    if (this.inputs.originalPrice) {
      this.inputs.originalPrice.addEventListener('input', triggerCalculation);
    }

    // Custom discount input
    if (this.inputs.customDiscount) {
      this.inputs.customDiscount.addEventListener('input', triggerCalculation);
    }
  }

  handleDiscountSelection(selectedBtn) {
    // Remove active class from all buttons
    this.discountButtons.forEach(btn => btn.classList.remove('active'));
    
    // Add active class to selected button
    selectedBtn.classList.add('active');
    
    const discount = selectedBtn.getAttribute('data-discount');
    
    if (discount === 'custom') {
      this.isCustomDiscount = true;
      this.showCustomDiscountInput();
      // Use custom discount input value
      const customValue = parseFloat(this.inputs.customDiscount?.value) || 0;
      this.currentDiscount = customValue;
    } else {
      this.isCustomDiscount = false;
      this.hideCustomDiscountInput();
      this.currentDiscount = parseFloat(discount);
    }

    // Recalculate if form is valid
    if (this.isFormValid(false)) {
      this.calculateDiscount(true);
    }
  }

  showCustomDiscountInput() {
    if (this.customDiscountContainer) {
      this.customDiscountContainer.style.display = 'block';
      this.customDiscountContainer.classList.add('show');
      
      // Focus custom discount input
      setTimeout(() => {
        if (this.inputs.customDiscount) {
          this.inputs.customDiscount.focus();
        }
      }, 100);
    }
  }

  hideCustomDiscountInput() {
    if (this.customDiscountContainer) {
      this.customDiscountContainer.style.display = 'none';
      this.customDiscountContainer.classList.remove('show');
    }
  }

  validateInput(input) {
    const value = parseFloat(input.value);
    const field = input.closest('.form-field');
    
    // Remove previous error states
    input.classList.remove('error');
    field?.classList.remove('error');

    // Validate original price input
    if (input === this.inputs.originalPrice) {
      if (input.hasAttribute('required') && (!input.value || value <= 0)) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }

      if (input.value && (isNaN(value) || value < 0)) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }
    }

    // Validate custom discount input
    if (input === this.inputs.customDiscount && this.isCustomDiscount) {
      if (!input.value || isNaN(value) || value < 0 || value > 100) {
        input.classList.add('error');
        field?.classList.add('error');
        return false;
      }
      
      // Update current discount
      this.currentDiscount = value;
    }

    return true;
  }

  isFormValid(showErrors = true) {
    let isValid = true;

    // Validate original price
    if (this.inputs.originalPrice) {
      const priceValid = this.validateInput(this.inputs.originalPrice);
      if (!priceValid) {
        isValid = false;
      }
    }

    // Validate custom discount if applicable
    if (this.isCustomDiscount && this.inputs.customDiscount) {
      const customDiscountValid = this.validateInput(this.inputs.customDiscount);
      if (!customDiscountValid) {
        isValid = false;
      }
    }

    // Check if a valid discount is selected
    if (!this.isCustomDiscount && (!this.currentDiscount || this.currentDiscount <= 0)) {
      isValid = false;
    }

    return isValid;
  }

  calculateDiscount(silent = false) {
    if (!this.isFormValid()) {
      if (!silent) {
        this.showError('Lütfen tüm alanları doğru şekilde doldurun.');
      }
      return;
    }

    // Show loading state
    if (!silent) {
      this.calculateBtn.classList.add('loading');
    }

    // Get values
    const originalPrice = parseFloat(this.inputs.originalPrice.value) || 0;
    const discountPercentage = this.currentDiscount;

    // Calculate discount
    const discountAmount = originalPrice * (discountPercentage / 100);
    const finalPrice = originalPrice - discountAmount;

    // Update results
    setTimeout(() => {
      this.displayResults({
        originalPrice: originalPrice,
        discountPercentage: discountPercentage,
        discountAmount: discountAmount,
        finalPrice: finalPrice
      });

      if (!silent) {
        this.calculateBtn.classList.remove('loading');
        this.showResult();
      }
    }, silent ? 0 : 300);
  }

  displayResults(results) {
    if (this.resultElements.originalPrice) {
      this.resultElements.originalPrice.textContent = 
        this.formatCurrency(results.originalPrice);
    }

    if (this.resultElements.discountPercentage) {
      this.resultElements.discountPercentage.textContent = 
        `%${results.discountPercentage}`;
    }

    if (this.resultElements.discountAmount) {
      this.resultElements.discountAmount.textContent = 
        this.formatCurrency(results.discountAmount);
    }

    if (this.resultElements.finalPrice) {
      this.resultElements.finalPrice.textContent = 
        this.formatCurrency(results.finalPrice);
    }
  }

  formatCurrency(amount) {
    return `${amount.toFixed(2)} ₺`;
  }

  showResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'block';
      this.resultSection.classList.add('show');
      
      // Smooth scroll to result
      setTimeout(() => {
        this.resultSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
  }

  hideResult() {
    if (this.resultSection) {
      this.resultSection.style.display = 'none';
      this.resultSection.classList.remove('show');
    }
  }

  resetForm() {
    // Clear all inputs
    Object.values(this.inputs).forEach(input => {
      if (input) {
        input.value = '';
        input.classList.remove('error');
        const field = input.closest('.form-field');
        field?.classList.remove('error');
      }
    });

    // Reset discount selection to default (10%)
    this.discountButtons.forEach(btn => btn.classList.remove('active'));
    const defaultDiscountBtn = document.querySelector('.discount-btn[data-discount="10"]');
    if (defaultDiscountBtn) {
      defaultDiscountBtn.classList.add('active');
    }
    
    this.currentDiscount = 10;
    this.isCustomDiscount = false;
    this.hideCustomDiscountInput();

    // Hide result
    this.hideResult();

    // Focus first input
    if (this.inputs.originalPrice) {
      this.inputs.originalPrice.focus();
    }
  }




  showError(message) {
    // You can implement a toast/alert system here
    // For now, using browser alert
    alert(message);
  }
}

// Utility function to format numbers
function formatNumber(num, decimals = 2) {
  return parseFloat(num).toFixed(decimals);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new IndirımCalculator();
});

// Re-initialize for theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('indirim-calculator')) {
    new IndirımCalculator();
  }
});

// Handle theme editor unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId.includes('indirim-calculator')) {
    // Cleanup if needed
  }
});